import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { doc, getDoc, deleteDoc, updateDoc, arrayUnion, collection, addDoc, serverTimestamp } from "firebase/firestore";
import { ref, deleteObject } from "firebase/storage";
import { db, storage } from "../../../firebase";
import { useUserAuth } from "../../../context/userAuthContext";
import { GolfScoringProvider } from "./GolfScoringContext";
import GolfScoringButton from "./GolfScoringButton";
import ActivityAttendees from "./ActivityAttendees";
import ActivityTools from "./ActivityTools";
import ActivityJoinRequests from "./ActivityJoinRequests";
import {
  ArrowLeft,
  Globe,
  Lock,
  MapPin,
  Calendar,
  Clock,
  Share2,
  MoreVertical,
  Pencil,
  Trash2,
  X,
  Image as ImageIcon,
  Navigation2,
  Tag,
  CheckCircle,
  Loader2
} from "lucide-react";

const ActivityInfo = () => {
  const { uid } = useParams();
  const navigate = useNavigate();
  const { user } = useUserAuth();
  const [activity, setActivity] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [showMenu, setShowMenu] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showLeaveConfirm, setShowLeaveConfirm] = useState(false);
  const [deletingActivity, setDeletingActivity] = useState(false);
  const [imageError, setImageError] = useState(false);
  const [joiningState, setJoiningState] = useState("idle"); // idle, joining, joined, requested, leaving
  const [showRequestModal, setShowRequestModal] = useState(false);
  const [requestMessage, setRequestMessage] = useState("");
  const [submittingRequest, setSubmittingRequest] = useState(false);

  // Hardcoded coordinates (you might want to get these from the activity object)
  const coordinates = {
    lat: 29.106360194695505,
    lng: -110.93477210180153
  };

  const openGoogleMaps = () => {
    const googleMapsUrl = `https://www.google.com/maps/search/?api=1&query=${coordinates.lat},${coordinates.lng}`;
    window.open(googleMapsUrl, '_blank');
  };

  const fetchActivity = async () => {
    try {
      const docRef = doc(db, "activities", uid);
      const docSnap = await getDoc(docRef);

      if (docSnap.exists()) {
        const activityData = { id: docSnap.id, ...docSnap.data() };
        setActivity(activityData);

        // Check if user is already an attendee
        if (activityData.attendees && activityData.attendees.includes(user?.uid)) {
          setJoiningState("joined");
        }

        // Check if user has already requested to join
        if (activityData.joinRequests && activityData.joinRequests.some(req => req.userId === user?.uid)) {
          setJoiningState("requested");
        }
      } else {
        setError("Activity not found");
      }
    } catch (err) {
      console.error("Error fetching activity:", err);
      setError("Error loading activity");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (user) {
      fetchActivity();
    } else {
      setLoading(false);
      setError("Please log in to view this activity");
    }
  }, [uid, user]);

  const handleDeleteActivity = async () => {
    try {
      setDeletingActivity(true);

      // Primero eliminar la imagen de Storage si existe
      if (activity.coverImage) {
        const imageRef = ref(storage, `activities/${uid}/cover`);
        try {
          await deleteObject(imageRef);
        } catch (error) {
          console.error("Error deleting image:", error);
        }
      }

      // Luego eliminar el documento
      const activityRef = doc(db, "activities", uid);
      await deleteDoc(activityRef);
      navigate("/myactivities", { replace: true });
    } catch (err) {
      console.error("Error deleting activity:", err);
      setError("Failed to delete activity. Please try again.");
      setDeletingActivity(false);
    }
  };

  const handleJoinActivity = async () => {
    if (!user) {
      navigate('/login', { state: { returnUrl: `/go/${uid}` } });
      return;
    }

    if (joiningState === "joined" || joiningState === "requested") {
      return;
    }

    // Si la actividad es pública, unirse directamente
    if (activity.isPublic) {
      try {
        setJoiningState("joining");

        const activityRef = doc(db, "activities", uid);

        // Verificar si hay espacio disponible
        if (activity.attendees && activity.attendees.length >= activity.maxAttendees) {
          setError("This activity is already full");
          setJoiningState("idle");
          return;
        }

        // Actualizar la lista de asistentes
        await updateDoc(activityRef, {
          attendees: arrayUnion(user.uid)
        });

        // Actualizar state local
        setJoiningState("joined");
        setActivity(prev => ({
          ...prev,
          attendees: [...prev.attendees, user.uid]
        }));

      } catch (err) {
        console.error("Error joining activity:", err);
        setError("Failed to join activity. Please try again.");
        setJoiningState("idle");
      }
    } else {
      // Si la actividad es privada, mostrar modal para solicitar unirse
      setShowRequestModal(true);
    }
  };

  const handleLeaveActivity = async () => {
    if (!user || joiningState !== "joined") return;

    if (!showLeaveConfirm) {
      setShowLeaveConfirm(true);
      return;
    }

    try {
      setJoiningState("leaving");
      setShowLeaveConfirm(false);

      const activityRef = doc(db, "activities", uid);

      // Obtener los datos actuales de la actividad
      const activityDoc = await getDoc(activityRef);
      if (!activityDoc.exists()) {
        throw new Error("Activity not found");
      }

      const activityData = activityDoc.data();
      const updatedAttendees = activityData.attendees.filter(id => id !== user.uid);

      // Actualizar la lista de asistentes
      await updateDoc(activityRef, {
        attendees: updatedAttendees
      });

      // Actualizar state local
      setJoiningState("idle");
      setActivity(prev => ({
        ...prev,
        attendees: updatedAttendees
      }));

    } catch (err) {
      console.error("Error leaving activity:", err);
      setError("Failed to leave the activity. Please try again.");
      setJoiningState("joined");
    }
  };

  const handleSubmitJoinRequest = async () => {
    if (!user) return;

    try {
      setSubmittingRequest(true);

      // Crear una solicitud de unión en Firestore
      const joinRequest = {
        userId: user.uid,
        userName: user.displayName || "Anonymous User",
        userPhoto: user.photoURL || "",
        activityId: uid,
        message: requestMessage,
        status: "pending", // pending, approved, rejected
        createdAt: serverTimestamp()
      };

      // Guardar en una colección de solicitudes
      await addDoc(collection(db, "joinRequests"), joinRequest);

      // También actualizar el documento de la actividad para referencia rápida
      const activityRef = doc(db, "activities", uid);
      await updateDoc(activityRef, {
        joinRequests: arrayUnion({
          userId: user.uid,
          status: "pending",
          timestamp: new Date().toISOString()
        })
      });

      setJoiningState("requested");
      setShowRequestModal(false);

    } catch (err) {
      console.error("Error requesting to join:", err);
      setError("Failed to send join request. Please try again.");
    } finally {
      setSubmittingRequest(false);
    }
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showMenu && !event.target.closest('.menu-container')) {
        setShowMenu(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showMenu]);

  const OptionsMenu = () => (
    <div className="absolute top-12 right-3 bg-white rounded-xl shadow-lg py-2 min-w-[160px] z-50 menu-container">
      <button
        onClick={() => {
          setShowMenu(false);
          navigate(`/edit/${activity.id}`);
        }}
        className="w-full px-4 py-2 text-left text-sm hover:bg-gray-100 flex items-center gap-2"
      >
        <Pencil className="w-4 h-4" />
        Edit Activity
      </button>
      <button
        onClick={() => {
          setShowMenu(false);
          setShowDeleteConfirm(true);
        }}
        className="w-full px-4 py-2 text-left text-sm text-red-600 hover:bg-gray-100 flex items-center gap-2"
      >
        <Trash2 className="w-4 h-4" />
        Delete Activity
      </button>
    </div>
  );

  const DeleteConfirmation = () => (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl max-w-sm w-full p-4">
        <div className="flex justify-between items-start mb-4">
          <h3 className="text-lg font-medium">Delete Activity</h3>
          <button
            onClick={() => setShowDeleteConfirm(false)}
            className="p-1 hover:bg-gray-100 rounded-full"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
        <p className="text-gray-600 mb-6">
          Are you sure you want to delete this activity? This action cannot be undone.
        </p>
        <div className="flex gap-3">
          <button
            onClick={() => setShowDeleteConfirm(false)}
            className="flex-1 py-2 px-4 border border-gray-200 rounded-lg hover:bg-gray-50"
            disabled={deletingActivity}
          >
            Cancel
          </button>
          <button
            onClick={handleDeleteActivity}
            disabled={deletingActivity}
            className="flex-1 py-2 px-4 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50"
          >
            {deletingActivity ? "Deleting..." : "Delete"}
          </button>
        </div>
      </div>
    </div>
  );

  const LeaveConfirmation = () => (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl max-w-sm w-full p-4">
        <div className="flex justify-between items-start mb-4">
          <h3 className="text-lg font-medium">Leave Activity</h3>
          <button
            onClick={() => setShowLeaveConfirm(false)}
            className="p-1 hover:bg-gray-100 rounded-full"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
        <p className="text-gray-600 mb-6">
          Are you sure you want to leave this activity? You can join again later if there's still space available.
        </p>
        <div className="flex gap-3">
          <button
            onClick={() => setShowLeaveConfirm(false)}
            className="flex-1 py-2 px-4 border border-gray-200 rounded-lg hover:bg-gray-50"
            disabled={joiningState === "leaving"}
          >
            Cancel
          </button>
          <button
            onClick={handleLeaveActivity}
            disabled={joiningState === "leaving"}
            className="flex-1 py-2 px-4 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50"
          >
            {joiningState === "leaving" ? (
              <span className="flex items-center justify-center">
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Leaving...
              </span>
            ) : (
              "Leave"
            )}
          </button>
        </div>
      </div>
    </div>
  );

  const RequestJoinModal = () => (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl max-w-sm w-full p-4">
        <div className="flex justify-between items-start mb-4">
          <h3 className="text-lg font-medium">Request to Join</h3>
          <button
            onClick={() => setShowRequestModal(false)}
            className="p-1 hover:bg-gray-100 rounded-full"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
        <p className="text-gray-600 mb-4">
          This is a private activity. Send a request to the organizer to join.
        </p>
        <div className="mb-4">
          <label htmlFor="requestMessage" className="block text-sm font-medium text-gray-700 mb-1">
            Message (optional)
          </label>
          <textarea
            id="requestMessage"
            rows="3"
            value={requestMessage}
            onChange={(e) => setRequestMessage(e.target.value)}
            placeholder="Introduce yourself or explain why you'd like to join..."
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500"
          />
        </div>
        <div className="flex gap-3">
          <button
            onClick={() => setShowRequestModal(false)}
            className="flex-1 py-2 px-4 border border-gray-200 rounded-lg hover:bg-gray-50"
            disabled={submittingRequest}
          >
            Cancel
          </button>
          <button
            onClick={handleSubmitJoinRequest}
            disabled={submittingRequest}
            className="flex-1 py-2 px-4 bg-orange-500 text-white rounded-lg hover:bg-orange-600 disabled:opacity-50"
          >
            {submittingRequest ? (
              <span className="flex items-center justify-center">
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Sending...
              </span>
            ) : (
              "Send Request"
            )}
          </button>
        </div>
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-500" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <p className="text-red-500 mb-4">{error}</p>
          <button
            onClick={() => navigate(-1)}
            className="text-orange-500 hover:text-orange-600"
          >
            Go Back
          </button>
        </div>
      </div>
    );
  }

  if (!activity) return null;

  const eventDate = new Date(activity.eventDateTime);
  const formattedDate = eventDate.toLocaleDateString('en-US', {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });
  const formattedTime = eventDate.toLocaleTimeString('en-US', {
    hour: '2-digit',
    minute: '2-digit'
  });

  // Check if activity is full
  const isActivityFull = activity.attendees && activity.attendees.length >= activity.maxAttendees;

  return (
    <div className="min-h-screen w-full bg-gray-50">
      {/* Header con imagen - pantalla completa */}
      <div className="relative w-full">
        {/* Imagen de portada */}
        <div className="relative w-full h-64 md:h-80 lg:h-96">
          {activity.coverImage && !imageError ? (
            <img
              src={activity.coverImage}
              alt={activity.title}
              className="w-full h-full object-cover"
              onError={() => setImageError(true)}
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center bg-gray-100">
              <ImageIcon className="w-12 h-12 text-gray-400" />
            </div>
          )}
          {/* Overlay gradiente */}
          <div className="absolute inset-0 bg-gradient-to-b from-black/50 to-transparent">
            <div className="max-w-7xl mx-auto w-full flex items-center justify-between py-4 px-4 md:px-8">
              <button
                onClick={() => navigate(-1)}
                className="p-2 hover:bg-white/10 rounded-full transition-colors text-white"
              >
                <ArrowLeft className="w-6 h-6" />
              </button>
              <div className="flex gap-3">
                <button
                  className="p-2 hover:bg-white/10 rounded-full transition-colors text-white"
                  onClick={() => alert('Share functionality coming soon!')}
                >
                  <Share2 className="w-6 h-6" />
                </button>
                {activity.creatorUID === user?.uid && (
                  <div className="relative menu-container">
                    <button
                      onClick={() => setShowMenu(!showMenu)}
                      className="p-2 hover:bg-white/10 rounded-full transition-colors text-white"
                    >
                      <MoreVertical className="w-6 h-6" />
                    </button>
                    {showMenu && <OptionsMenu />}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 md:px-8 py-6 md:py-10 grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8">
        {/* Columna Principal */}
        <div className="md:col-span-2 space-y-6">
          {/* Title Section */}
          <div className="bg-white p-6 rounded-xl">
            <h1 className="text-2xl md:text-3xl font-bold mb-4">{activity.title}</h1>
            <div className="flex flex-wrap items-center gap-3">
              <div className="flex items-center gap-2 text-sm md:text-base text-gray-500">
                {activity.isPublic ? (
                  <>
                    <Globe className="w-5 h-5 text-green-500" />
                    <span>Public Activity</span>
                  </>
                ) : (
                  <>
                    <Lock className="w-5 h-5 text-blue-500" />
                    <span>Private Activity</span>
                  </>
                )}
              </div>
              {activity.category && (
                <div className="flex items-center gap-2 text-sm md:text-base text-gray-600 bg-gray-100 px-3 py-1 rounded-md">
                  <Tag className="w-5 h-5 text-gray-500" />
                  <span>{activity.category}</span>
                </div>
              )}
            </div>
          </div>

          {/* Location and Time */}
          <div className="bg-white p-6 rounded-xl">
            <div className="space-y-5">
              <div className="flex items-start gap-4">
                <MapPin className="w-6 h-6 text-gray-400 flex-shrink-0 mt-1" />
                <div className="flex-grow">
                  <p className="text-sm text-gray-600 mb-1">Location</p>
                  <p className="font-medium text-lg">{activity.location}</p>
                </div>
                <button
                  onClick={openGoogleMaps}
                  className="bg-orange-500 text-white px-4 py-2 rounded-lg flex items-center gap-2 hover:bg-orange-600 transition-colors"
                >
                  <Navigation2 className="w-5 h-5" />
                  Navigate
                </button>
              </div>

              <div className="flex items-start gap-4">
                <Calendar className="w-6 h-6 text-gray-400 flex-shrink-0 mt-1" />
                <div>
                  <p className="text-sm text-gray-600 mb-1">Date</p>
                  <p className="font-medium text-lg">{formattedDate}</p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <Clock className="w-6 h-6 text-gray-400 flex-shrink-0 mt-1" />
                <div>
                  <p className="text-sm text-gray-600 mb-1">Time</p>
                  <p className="font-medium text-lg">{formattedTime}</p>
                </div>
              </div>
            </div>
          </div>

          {/* Description */}
          <div className="bg-white p-6 rounded-xl">
            <h2 className="text-xl font-semibold mb-4">Description</h2>
            <p className="whitespace-pre-wrap text-gray-700 leading-relaxed">{activity.description}</p>
          </div>

          {/* Golf Scoring Section */}
          {activity.category === 'Golf' && (
            <div className="bg-white p-6 rounded-xl">
              <h2 className="text-xl font-semibold mb-4">Golf Scoring</h2>
              <GolfScoringProvider activityId={uid}>
                <GolfScoringButton activityId={uid} />
              </GolfScoringProvider>
            </div>
          )}

          {/* Activity Tools */}
          <div className="bg-white p-6 rounded-xl">
            <ActivityTools activityId={uid} attendees={activity.attendees} />
          </div>
        </div>

        {/* Columna Lateral */}
        <div className="space-y-6">
          {/* Attendees Component */}
          <div className="bg-white p-6 rounded-xl">
            <ActivityAttendees activityId={uid} maxAttendees={activity.maxAttendees} />
          </div>

          {/* Join Requests - Solo visible para el creador */}
          {activity.creatorUID === user?.uid && (
            <div className="bg-white p-6 rounded-xl">
              <ActivityJoinRequests
                activityId={uid}
                isCreator={activity.creatorUID === user?.uid}
              />
            </div>
          )}
        </div>
      </div>

      {/* Join Button (if not creator) - Fixed at bottom */}
      {activity.creatorUID !== user?.uid && (
        <div className="fixed bottom-0 left-0 right-0 p-4 md:p-6 bg-white border-t border-gray-100 shadow-lg z-10">
          <div className="max-w-7xl mx-auto">
            {joiningState === "joined" ? (
              <button
                onClick={handleLeaveActivity}
                className="w-full bg-red-500 text-white py-3 md:py-4 rounded-xl font-medium hover:bg-red-600 transition-colors flex items-center justify-center gap-2 text-lg"
              >
                <X className="w-6 h-6" />
                Leave Event
              </button>
            ) : joiningState === "requested" ? (
              <button
                className="w-full bg-blue-500 text-white py-3 md:py-4 rounded-xl font-medium flex items-center justify-center gap-2 text-lg"
                disabled
              >
                <Clock className="w-6 h-6" />
                Request Pending
              </button>
            ) : joiningState === "joining" ? (
              <button
                className="w-full bg-orange-400 text-white py-3 md:py-4 rounded-xl font-medium flex items-center justify-center gap-2 text-lg"
                disabled
              >
                <Loader2 className="w-6 h-6 animate-spin" />
                Joining...
              </button>
            ) : joiningState === "leaving" ? (
              <button
                className="w-full bg-red-400 text-white py-3 md:py-4 rounded-xl font-medium flex items-center justify-center gap-2 text-lg"
                disabled
              >
                <Loader2 className="w-6 h-6 animate-spin" />
                Leaving...
              </button>
            ) : isActivityFull ? (
              <button
                className="w-full bg-gray-400 text-white py-3 md:py-4 rounded-xl font-medium text-lg"
                disabled
              >
                Activity Full
              </button>
            ) : (
              <button
                onClick={handleJoinActivity}
                className="w-full bg-orange-500 text-white py-3 md:py-4 rounded-xl font-medium hover:bg-orange-600 transition-colors text-lg"
              >
                Join Activity
              </button>
            )}
          </div>
        </div>
      )}

      {/* Delete Confirmation Dialog */}
      {showDeleteConfirm && <DeleteConfirmation />}

      {/* Leave Confirmation Dialog */}
      {showLeaveConfirm && <LeaveConfirmation />}

      {/* Request to Join Modal */}
      {showRequestModal && <RequestJoinModal />}
    </div>
  );
};

export default ActivityInfo;